// src/components/LoginComponent.vue

<template>
  <div>
    <!-- Simple form for email and password -->
    <v-container>
      <v-text-field
        label="Email"
        v-model="email"
        outlined
      ></v-text-field>
      <v-text-field
        label="Password"
        v-model="password"
        type="password"
        outlined
        @keyup.enter="signIn"
      ></v-text-field>

      <!-- Sign In and New User buttons -->
      <v-btn @click="signIn">Sign In</v-btn>
      <v-btn @click="createAccount">New User</v-btn>

      <!-- Display error message if verification or account creation fails -->
      <v-alert v-if="errorMessage" type="error">{{ errorMessage }}</v-alert>
      
      <!-- Forgot password option -->
      <v-btn text @click="showResetPasswordDialog = true">Change Password</v-btn>

      <!-- Error alert -->
      <v-alert
        v-if="showError"
        type="error"
        :variant="$vuetify.display.mobile ? 'tonal' : 'plain'"
        closable
        density="comfortable"
        class="mt-4 error-alert"
        @click:close="showError = false"
      >
        {{ getErrorMessage(errorCode) }}
      </v-alert>
    </v-container>

    <!-- Password Reset Dialog -->
    <ResetPasswordDialog v-if="showResetPasswordDialog" @close="showResetPasswordDialog = false" />
  </div>
</template>

<script>
import { auth } from '../firebase'; // Adjust the path based on your setup
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import ResetPasswordDialog from './ResetPasswordDialog.vue'; // Assuming you have a component for resetting passwords
import axios from 'axios'; // Import axios for making HTTP requests
import { mapActions } from 'vuex';
import { initialCompanyState, initialUserState } from '@/models/user.js';

export default {
  name: 'LoginComponent',
  components: {
    ResetPasswordDialog,
  },
  data() {
    return {
      showError: false,
      errorCode: null,
      email: '',
      password: '',
      showResetPasswordDialog: false,
      errorMessage: '', // To display error messages
    };
  },
  methods: {
    ...mapActions(['setUser']), // Map Vuex actions

    getErrorMessage(code) {
      console.log('error code: ', code);
      const errorMessages = {
        'auth/invalid-email': 'Please enter a valid email address',
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/too-many-requests': 'Too many login attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        'auth/missing-password' : 'Enter a password.',
        'auth/invalid-credential' : 'Is this your first time logging in? Make up a password and click NEW USER.',
        'ERR_BAD_REQUEST' : 'A user with that email address was not found. Ask your administrator to add a user for you.'
      }
      return errorMessages[code] || 'An error occurred during sign in'
    },

    async signIn() {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password);
        const user = userCredential.user;

        // Fetch additional user data
        const additionalUserData = await this.fetchAdditionalUserData(user.uid);
        delete additionalUserData._id;

        const companyData = await this.fetchCompanyData(additionalUserData.companyNumber);
        delete companyData._id;

        // Dispatch the setUser action with the additional user data
        this.$store.commit('setUser', { ...initialUserState, ...user, ...additionalUserData });
        this.$store.dispatch('setSelectedUser', { ...initialUserState, uid:user.uid, ...additionalUserData });
        this.$store.dispatch('setSelectedCompany', companyData);

        console.log('User signed in successfully:', user);
      } catch (error) {
        // console.error('Error signing in:', error.message);
        // this.errorMessage = 'Sign in failed: ' + error.message;
        this.errorCode = error.code
        this.showError = true
      }
    },

    async createAccount() {
      try {
        // Step 1: Verify the user using the `/verify` endpoint
        console.log('Starting user verification for:', this.email);
        const response = await axios.post(`${process.env.VUE_APP_THIS_SERVER}/portaldb/db/verify`, {
          email: this.email
        });

        if (response.data.success) {
          console.log('User verification successful:', response.data);

          // Step 2: Create the user in Firebase
          console.log('Creating Firebase user...');
          const userCredential = await createUserWithEmailAndPassword(auth, this.email, this.password);
          const user = userCredential.user;

          // Step 3: Update MongoDB with the Firebase uid
          console.log('Updating MongoDB user with UID:', user.uid);
          await this.updateMongoUserWithUid(user.uid);

          // Step 4: Fetch additional user data
          console.log('Fetching additional user data for:', user.uid);
          const additionalUserData = await this.fetchAdditionalUserData(user.uid); // could this not just be get /user/:uid ?
          delete additionalUserData._id;

          console.log('Additional user data fetched:', additionalUserData);
          const companyData = await this.fetchCompanyData(additionalUserData.companyNumber);
          delete companyData._id;

          // Step 5: Dispatch the setUser action with the additional user data
          this.$store.dispatch('setUser', { ...user, ...additionalUserData });
          this.$store.dispatch('setSelectedUser', { ...initialUserState, uid:user.uid, ...additionalUserData });
          this.$store.dispatch('setSelectedCompany', companyData);

          console.log('User data stored in Vuex:', this.$store.getters.getUserData);

          // Clear error message on success
          this.errorMessage = '';
        }
      } catch (error) {
          this.errorCode = error.code
          this.showError = true

        // // Handle verification or account creation failure
        // if (error.response && error.response.data) {
        //   this.errorMessage = error.response.data.message;
        // } else {
        //   this.errorMessage = 'Account creation failed: ' + error.message;
        // }
        // console.error('Error during account creation:', error.message);
      }
    },

    async updateMongoUserWithUid(uid) {
      try {
        console.log('Updating MongoDB user with UID:', {
          email: this.email,
          uid: uid,
        });

        const updateResponse = await axios.post(`${process.env.VUE_APP_THIS_SERVER}/portaldb/db/update-uid`, {
          email: this.email,
          uid: uid,
        });

        console.log('MongoDB update response:', updateResponse.data);

        if (updateResponse.data.success) {
          console.log('MongoDB user UID updated successfully');
        } else {
          console.error('Failed to update MongoDB user UID:', updateResponse.data.message);
        }
      } catch (error) {
        console.error('Error updating MongoDB user UID:', error.message);
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
        }
      }
    },
    
    async fetchAdditionalUserData(userId) {
      try {
        console.log('Attempting to fetch additional user data for userId:', userId);
        const response = await axios.get(`${process.env.VUE_APP_THIS_SERVER}/portaldb/db/user/${userId}`);
        console.log('Fetch additional user data response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching additional user data:', error.message);
        return { ...initialUserState };
      }
    },

    async fetchCompanyData(companyNumber) {
      try {
        console.log('Fetching company info for company number ' + companyNumber);
        const response = await axios.get(`${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${companyNumber}`);
        return response.data
      } catch (error) {
        console.error('Error fetching company by number: ', error.message);
        return { ...initialCompanyState };
      }
    }

  },
};
</script>

<style scoped>

.v-alert {
  opacity: 1.0;
}
  
.error-alert {
  background: white;
  color: rgba(0, 0, 0, 1.0);
}

@media (max-width: 600px) {

  .error-alert {
    position: absolute;
    left: 10px;
    top: 10px;
    background: #FFF3E0;
    color: inherit;
    width: 90%;
    margin: 4px 0;
    font-size: 14px;
  }
}
</style>
